import { useState, useEffect, useRef } from "react"
import { Button, Form, Image, Collapse } from 'react-bootstrap'
import { parseKle } from "./KLEParser"
import { buildPlate } from "./PlateBuilder"
import { buildPads } from "./PadBuilder"
import { buildFoams as buildFoamskh } from "./FoamBuilderkh"
import { buildFoams as buildFoamsgt } from "./FoamBuildergt"
import { buildCase } from "./CaseBuilder"
import { StabilizerMXBasic as StabilizerJxm } from "./cutouts/Stabilizerjxm"
import Decimal from 'decimal.js'
import makerjs from 'makerjs'
import fileDownload from 'js-file-download'
import logo from './logo.png'
import { DataHelpPane, SwitchCutoutPane, OtherCutoutPane, AdvancedPane, AboutPane } from './HelpPanes'
import ThreePreview from './ThreePreview'
import './App.css'

// ----- 自定义滑动开关（文字显示在轨道两侧）-----
const SwitchWithStatus = ({ checked, onChange, leftLabel = '开', rightLabel = '合' }) => {
  const handleClick = () => {
    onChange(!checked);
  };
  return (
    <div className="custom-switch-container" onClick={handleClick}>
      <div className={`custom-switch-track ${checked ? 'checked' : ''}`}>
        <span className={`switch-label switch-label-left ${!checked ? 'active' : ''}`}>{leftLabel}</span>
        <span className={`switch-label switch-label-right ${checked ? 'active' : ''}`}>{rightLabel}</span>
        <div className="custom-switch-thumb"></div>
      </div>
    </div>
  );
};

// ----- 可滚轮调节的数字输入框（仅在聚焦时滚轮调节，未聚焦时正常滚动页面）-----
const NumberInput = ({
  value,
  onChange,
  step = 1,
  min = -Infinity,
  max = Infinity,
  precision = null,
  size = "sm",
  style,
  ...rest
}) => {
  const inputRef = useRef(null)

  useEffect(() => {
    const el = inputRef.current
    if (!el) return

    const handleWheel = (e) => {
      // 仅在输入框聚焦时拦截滚轮并调节数值，否则让页面正常滚动
      if (document.activeElement !== el) return

      e.preventDefault()
      e.stopPropagation()

      const current = parseFloat(value)
      const base = isNaN(current) ? 0 : current
      const delta = e.deltaY > 0 ? -step : step
      let next = base + delta

      if (next < min) next = min
      if (next > max) next = max
      if (precision !== null) {
        next = parseFloat(next.toFixed(precision))
      }

      onChange({ target: { value: next } })
    }

    el.addEventListener('wheel', handleWheel, { passive: false })
    return () => el.removeEventListener('wheel', handleWheel)
  }, [value, onChange, step, min, max, precision])

  return (
    <Form.Control
      ref={inputRef}
      type="number"
      size={size}
      value={value}
      onChange={onChange}
      step={step}
      min={min === -Infinity ? undefined : min}
      max={max === Infinity ? undefined : max}
      style={style}
      {...rest}
    />
  )
}

function App() {

  const [kleText, setKleText] = useState("")
  const [previewSvg, setPreviewSvg] = useState(null)
  const [plateSvg, setPlateSvg] = useState(null)
  const [plateDxf, setPlateDxf] = useState(null)

  const [kleData, setKleData] = useState(null)

  const [switchCutoutType, setSwitchCutoutType] = useState("mx-basic")
  const [stabilizerCutoutType, setStabilizerCutoutType] = useState("mx-spec")
  const [acousticCutoutType, setAcousticCutoutType] = useState("none")
  const [switchSocketType, setSwitchSocketType] = useState("kailh")

  const [switchRadius, setSwitchRadius] = useState(0.5)
  const [stabilizerRadius, setStabilizerRadius] = useState(0.5)
  const [acousticRadius, setAcousticRadius] = useState(0.5)
  const [boardRadius, setBoardRadius] = useState(4)

  const [stabilizerRotation, setStabilizerRotation] = useState(0)
  const [mergeStabilizer, setMergeStabilizer] = useState(true)

  const [unitWidth, setUnitWidth] = useState(19.05)
  const [unitHeight, setUnitHeight] = useState(19.05)
  const [kerf, setKerf] = useState(0)
  const [lineWidth, setLineWidth] = useState(0.1)
  const [previewScale, setPreviewScale] = useState(1)
  const [previewPosition, setPreviewPosition] = useState({ x: 0, y: 0 })
  const [isDragging, setIsDragging] = useState(false)
  const dragStartRef = useRef({ x: 0, y: 0 })
  const previewContainerRef = useRef(null)

  const [layoutUnitSize, setLayoutUnitSize] = useState(40)
  const [layoutPadding, setLayoutPadding] = useState(10)

  const [selectedKeyIndex, setSelectedKeyIndex] = useState(null)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [helpOpen, setHelpOpen] = useState(false)
  const [plateDimensions, setPlateDimensions] = useState(null)
  const [padPreviewSvg, setPadPreviewSvg] = useState(null)
  const [padSvg, setPadSvg] = useState(null)
  const [padDxf, setPadDxf] = useState(null)
  const [padDimensions, setPadDimensions] = useState(null)
  const [foamPreviewSvg, setFoamPreviewSvg] = useState(null)
  const [foamSvg, setFoamSvg] = useState(null)
  const [foamDxf, setFoamDxf] = useState(null)
  const [foamDimensions, setFoamDimensions] = useState(null)
  const [sandwichPreviewSvg, setSandwichPreviewSvg] = useState(null)
  const [sandwichSvg, setSandwichSvg] = useState(null)
  const [sandwichDxf, setSandwichDxf] = useState(null)
  const [sandwichDimensions, setSandwichDimensions] = useState(null)
  const [previewMode, setPreviewMode] = useState('plate')
  const [ledPosition, setLedPosition] = useState('top')

  // 外壳设置状态
  const [caseEnabled, setCaseEnabled] = useState(false)
  const [caseBorderTop, setCaseBorderTop] = useState(7)
  const [caseBorderBottom, setCaseBorderBottom] = useState(7)
  const [caseBorderLeft, setCaseBorderLeft] = useState(7)
  const [caseBorderRight, setCaseBorderRight] = useState(7)
  const [caseInnerRadius, setCaseInnerRadius] = useState(1)
  // 开启后，定位板（及夹心棉）最外轮廓按外壳上下左右边框宽度向外偏移
  const [plateUseCaseBorders, setPlateUseCaseBorders] = useState(false)
  const [caseLayer1Svg, setCaseLayer1Svg] = useState(null)
  const [caseLayer2Svg, setCaseLayer2Svg] = useState(null)
  const [caseLayer3Svg, setCaseLayer3Svg] = useState(null)
  const [caseLayer1Dxf, setCaseLayer1Dxf] = useState(null)
  const [caseLayer2Dxf, setCaseLayer2Dxf] = useState(null)
  const [caseLayer3Dxf, setCaseLayer3Dxf] = useState(null)
  const [caseDimensions, setCaseDimensions] = useState(null)
  const [layer1Dimensions, setLayer1Dimensions] = useState(null)
  const [layer2Dimensions, setLayer2Dimensions] = useState(null)
  const [layer3Dimensions, setLayer3Dimensions] = useState(null)

  // 3D 预览设置
  const [preview3dEnabled, setPreview3dEnabled] = useState(false)
  const [preview3dThickness, setPreview3dThickness] = useState(1.5)
  const [preview3dColor, setPreview3dColor] = useState('#4f46e5')
  // 递增后触发 ThreePreview 重置相机位置与角度
  const [preview3dResetKey, setPreview3dResetKey] = useState(0)
  // 预览背景：true=浅色(默认)，false=深色；浅色时线稿改为深色
  const [previewLightBg, setPreviewLightBg] = useState(true)
  const previewStrokeColor = previewLightBg ? '#1a1a2e' : 'white'

  // 计算模型的路径总长度和面积
  const calculateModelMetrics = (model) => {
    try {
      let totalLength = 0
      const traverse = (m) => {
        if (m.paths) {
          Object.values(m.paths).forEach(path => {
            try {
              const len = makerjs.measure.pathLength(path)
              if (len && !isNaN(len)) totalLength += len
            } catch (e) { }
          })
        }
        if (m.models) {
          Object.values(m.models).forEach(traverse)
        }
      }
      traverse(model)

      const extents = makerjs.measure.modelExtents(model)
      const width = extents.high[0] - extents.low[0]
      const height = extents.high[1] - extents.low[1]
      const area = width * height

      return { width, height, area, totalLength }
    } catch (e) {
      console.error('计算指标失败', e)
      return null
    }
  }

  const generatePreviewSvg = (plateData) => {
    try {
      const metrics = calculateModelMetrics(plateData)
      if (!metrics) {
        setPlateDimensions(null)
        return null
      }
      const { width, height, area, totalLength } = metrics
      setPlateDimensions({ width, height, area, totalLength })
      const viewBox = `0 0 ${width} ${height}`
      return makerjs.exporter.toSVG(plateData, {
        stroke: previewStrokeColor,
        strokeWidth: `${lineWidth}mm`,
        svgAttrs: {
          width: '100%',
          height: '100%',
          viewBox: viewBox
        }
      })
    } catch (e) {
      console.error('生成预览SVG失败', e)
      setPlateDimensions(null)
      return null
    }
  }

  useEffect(() => {
    const kleReturn = parseKle(kleText)

    if (kleReturn && kleReturn.length > 0) {
      setKleData(kleReturn)
      setSelectedKeyIndex(null)

      try {
        const plateData = buildPlate(kleReturn, {
          switchCutoutType: switchCutoutType,
          stabilizerCutoutType: stabilizerCutoutType,
          acousticCutoutType: acousticCutoutType,
          switchFilletRadius: new Decimal(switchRadius),
          stabilizerFilletRadius: new Decimal(stabilizerRadius),
          acousticFilletRadius: new Decimal(acousticRadius),
          boardRadius: new Decimal(boardRadius),
          unitWidth: new Decimal(unitWidth),
          unitHeight: new Decimal(unitHeight),
          kerf: new Decimal(kerf),
          stabilizerRotation: new Decimal(stabilizerRotation),
          mergeStabilizer: mergeStabilizer,
          plateUseCaseBorders: caseEnabled && plateUseCaseBorders,
          borderTop: new Decimal(caseBorderTop),
          borderBottom: new Decimal(caseBorderBottom),
          borderLeft: new Decimal(caseBorderLeft),
          borderRight: new Decimal(caseBorderRight),
        })

        const previewSvgData = generatePreviewSvg(plateData)
        setPreviewSvg(previewSvgData)

        const svgData = makerjs.exporter.toSVG(plateData, { units: makerjs.unitType.Millimeter, strokeWidth: `${lineWidth}mm` })
        setPlateSvg(svgData)
        const dxfData = makerjs.exporter.toDXF(plateData, { units: makerjs.unitType.Millimeter })
        setPlateDxf(dxfData)

        // 生成轴下垫
        try {
          const padData = buildPads(kleReturn, {
            unitWidth: new Decimal(unitWidth),
            unitHeight: new Decimal(unitHeight),
            extraRotation: ledPosition === 'bottom' ? 180 : 0,
            boardRadius: new Decimal(boardRadius),
          })
          const padMetrics = calculateModelMetrics(padData)
          if (padMetrics) {
            setPadDimensions(padMetrics)
          } else {
            setPadDimensions(null)
          }
          const padPreview = makerjs.exporter.toSVG(padData, {
            stroke: previewStrokeColor,
            strokeWidth: `${lineWidth}mm`,
            svgAttrs: { width: '100%', height: '100%' }
          })
          setPadPreviewSvg(padPreview)
          setPadSvg(makerjs.exporter.toSVG(padData, { units: makerjs.unitType.Millimeter, strokeWidth: `${lineWidth}mm` }))
          setPadDxf(makerjs.exporter.toDXF(padData, { units: makerjs.unitType.Millimeter }))
        } catch (padErr) {
          console.log('轴下垫生成失败', padErr)
          setPadPreviewSvg(null)
          setPadSvg(null)
          setPadDxf(null)
          setPadDimensions(null)
        }

        // 生成底棉
        try {
          const buildFoamsFn = switchSocketType === 'gateron' ? buildFoamsgt : buildFoamskh
          const foamData = buildFoamsFn(kleReturn, {
            unitWidth: new Decimal(unitWidth),
            unitHeight: new Decimal(unitHeight),
            extraRotation: ledPosition === 'bottom' ? 180 : 0,
            boardRadius: new Decimal(boardRadius),
          })
          const foamMetrics = calculateModelMetrics(foamData)
          if (foamMetrics) {
            setFoamDimensions(foamMetrics)
          } else {
            setFoamDimensions(null)
          }
          const foamPreview = makerjs.exporter.toSVG(foamData, {
            stroke: previewStrokeColor,
            strokeWidth: `${lineWidth}mm`,
            svgAttrs: { width: '100%', height: '100%' }
          })
          setFoamPreviewSvg(foamPreview)
          setFoamSvg(makerjs.exporter.toSVG(foamData, { units: makerjs.unitType.Millimeter, strokeWidth: `${lineWidth}mm` }))
          setFoamDxf(makerjs.exporter.toDXF(foamData, { units: makerjs.unitType.Millimeter }))
        } catch (foamErr) {
          console.log('底棉生成失败', foamErr)
          setFoamPreviewSvg(null)
          setFoamSvg(null)
          setFoamDxf(null)
          setFoamDimensions(null)
        }

        // 生成夹心棉
        try {
          const sandwichData = buildPlate(kleReturn, {
            switchCutoutType: switchCutoutType,
            stabilizerCutoutType: stabilizerCutoutType,
            acousticCutoutType: acousticCutoutType,
            switchFilletRadius: new Decimal(switchRadius),
            stabilizerFilletRadius: new Decimal(stabilizerRadius),
            acousticFilletRadius: new Decimal(acousticRadius),
            boardRadius: new Decimal(boardRadius),
            unitWidth: new Decimal(unitWidth),
            unitHeight: new Decimal(unitHeight),
            kerf: new Decimal(kerf),
            stabilizerRotation: new Decimal(stabilizerRotation),
            mergeStabilizer: mergeStabilizer,
            customStabilizerGenerator: new StabilizerJxm(),
            plateUseCaseBorders: caseEnabled && plateUseCaseBorders,
            borderTop: new Decimal(caseBorderTop),
            borderBottom: new Decimal(caseBorderBottom),
            borderLeft: new Decimal(caseBorderLeft),
            borderRight: new Decimal(caseBorderRight),
          })
          const sandwichMetrics = calculateModelMetrics(sandwichData)
          if (sandwichMetrics) {
            setSandwichDimensions(sandwichMetrics)
          } else {
            setSandwichDimensions(null)
          }
          const sandwichPreview = makerjs.exporter.toSVG(sandwichData, {
            stroke: previewStrokeColor,
            strokeWidth: `${lineWidth}mm`,
            svgAttrs: { width: '100%', height: '100%' }
          })
          setSandwichPreviewSvg(sandwichPreview)
          setSandwichSvg(makerjs.exporter.toSVG(sandwichData, { units: makerjs.unitType.Millimeter, strokeWidth: `${lineWidth}mm` }))
          setSandwichDxf(makerjs.exporter.toDXF(sandwichData, { units: makerjs.unitType.Millimeter }))
        } catch (sandwichErr) {
          console.log('夹心棉生成失败', sandwichErr)
          setSandwichPreviewSvg(null)
          setSandwichSvg(null)
          setSandwichDxf(null)
          setSandwichDimensions(null)
        }

        // 生成外壳
        if (caseEnabled) {
          try {
            const caseData = buildCase(kleReturn, {
              unitWidth: new Decimal(unitWidth),
              unitHeight: new Decimal(unitHeight),
              borderTop: new Decimal(caseBorderTop),
              borderBottom: new Decimal(caseBorderBottom),
              borderLeft: new Decimal(caseBorderLeft),
              borderRight: new Decimal(caseBorderRight),
              boardRadius: new Decimal(boardRadius),
              innerRadius: new Decimal(caseInnerRadius),
            })
            
            const layer1Preview = makerjs.exporter.toSVG(caseData.layer1, {
              stroke: previewStrokeColor,
              strokeWidth: `${lineWidth}mm`,
              svgAttrs: { width: '100%', height: '100%' }
            })
            setCaseLayer1Svg(layer1Preview)
            setCaseLayer1Dxf(makerjs.exporter.toDXF(caseData.layer1, { units: makerjs.unitType.Millimeter }))
            
            const layer2Preview = makerjs.exporter.toSVG(caseData.layer2, {
              stroke: previewStrokeColor,
              strokeWidth: `${lineWidth}mm`,
              svgAttrs: { width: '100%', height: '100%' }
            })
            setCaseLayer2Svg(layer2Preview)
            setCaseLayer2Dxf(makerjs.exporter.toDXF(caseData.layer2, { units: makerjs.unitType.Millimeter }))
            
            const layer3Preview = makerjs.exporter.toSVG(caseData.layer3, {
              stroke: previewStrokeColor,
              strokeWidth: `${lineWidth}mm`,
              svgAttrs: { width: '100%', height: '100%' }
            })
            setCaseLayer3Svg(layer3Preview)
            setCaseLayer3Dxf(makerjs.exporter.toDXF(caseData.layer3, { units: makerjs.unitType.Millimeter }))
            
            setCaseDimensions({
              width: caseData.dimensions.width,
              height: caseData.dimensions.height,
              plateWidth: caseData.dimensions.plateWidth,
              plateHeight: caseData.dimensions.plateHeight,
            })
            setLayer1Dimensions(caseData.layer1Dimensions)
            setLayer2Dimensions(caseData.layer2Dimensions)
            setLayer3Dimensions(caseData.layer3Dimensions)
          } catch (caseErr) {
            console.log('外壳生成失败', caseErr)
            setCaseLayer1Svg(null)
            setCaseLayer2Svg(null)
            setCaseLayer3Svg(null)
            setCaseLayer1Dxf(null)
            setCaseLayer2Dxf(null)
            setCaseLayer3Dxf(null)
            setCaseDimensions(null)
          }
        } else {
          setCaseLayer1Svg(null)
          setCaseLayer2Svg(null)
          setCaseLayer3Svg(null)
          setCaseLayer1Dxf(null)
          setCaseLayer2Dxf(null)
          setCaseLayer3Dxf(null)
          setCaseDimensions(null)
        }

      } catch (error) {
        console.log(error)
        setPreviewSvg(null)
        setPlateSvg(null)
        setPlateDxf(null)
        setPadPreviewSvg(null)
        setPadSvg(null)
        setPadDxf(null)
        setPadDimensions(null)
        setFoamPreviewSvg(null)
        setFoamSvg(null)
        setFoamDxf(null)
        setFoamDimensions(null)
        setSandwichPreviewSvg(null)
        setSandwichSvg(null)
        setSandwichDxf(null)
        setSandwichDimensions(null)
        setKleData(null)
      }

    } else {
      setPreviewSvg(null)
      setPlateSvg(null)
      setPlateDxf(null)
      setPadPreviewSvg(null)
      setPadSvg(null)
      setPadDxf(null)
      setPadDimensions(null)
      setFoamPreviewSvg(null)
      setFoamSvg(null)
      setFoamDxf(null)
      setFoamDimensions(null)
      setSandwichPreviewSvg(null)
      setSandwichSvg(null)
      setSandwichDxf(null)
      setSandwichDimensions(null)
      setKleData(null)
      setSelectedKeyIndex(null)
    }

  }, [
    kleText,
    switchCutoutType,
    stabilizerCutoutType,
    acousticCutoutType,
    switchRadius,
    stabilizerRadius,
    acousticRadius,
    boardRadius,
    unitWidth,
    unitHeight,
    kerf,
    lineWidth,
    stabilizerRotation,
    mergeStabilizer,
    ledPosition,
    switchSocketType,
    caseEnabled,
    caseBorderTop,
    caseBorderBottom,
    caseBorderLeft,
    caseBorderRight,
    caseInnerRadius,
    plateUseCaseBorders,
    previewLightBg,
  ])

  const downloadData = (fileData, extension) => {
    const date = new Date(Date.now())
    fileDownload(fileData, "定位板-" + date.toISOString() + extension)
  }

  const downloadPadData = (fileData, extension) => {
    const date = new Date(Date.now())
    fileDownload(fileData, "轴下垫-" + date.toISOString() + extension)
  }

  const downloadFoamData = (fileData, extension) => {
    const date = new Date(Date.now())
    fileDownload(fileData, "底棉-" + date.toISOString() + extension)
  }

  const downloadSandwichData = (fileData, extension) => {
    const date = new Date(Date.now())
    fileDownload(fileData, "夹心棉-" + date.toISOString() + extension)
  }

  const downloadCaseData = (fileData, extension, layerName) => {
    const date = new Date(Date.now())
    fileDownload(fileData, "外壳-" + layerName + "-" + date.toISOString() + extension)
  }

  // 使用 Blob URL 打开 CAD 编辑器，避免 GET 参数过长导致报错
  const openCadEditor = (dxfData) => {
    if (!dxfData) return

    const blob = new Blob([dxfData], { type: 'application/dxf' })
    const blobUrl = URL.createObjectURL(blob)

    window.open('./cad/?data=' + encodeURIComponent(blobUrl), '_blank')
  }

  const handleWheel = (e) => {
    e.preventDefault()
    const delta = e.deltaY > 0 ? -0.1 : 0.1
    setPreviewScale(prev => Math.max(0.1, Math.min(5, prev + delta)))
  }

  const handleMouseDown = (e) => {
    e.preventDefault()
    setIsDragging(true)
    dragStartRef.current = { x: e.clientX - previewPosition.x, y: e.clientY - previewPosition.y }
  }

  const handleMouseMove = (e) => {
    if (isDragging) {
      e.preventDefault()
      setPreviewPosition({
        x: e.clientX - dragStartRef.current.x,
        y: e.clientY - dragStartRef.current.y
      })
    }
  }

  const handleMouseUp = () => {
    setIsDragging(false)
  }

  useEffect(() => {
    const el = previewContainerRef.current
    if (!el) return
    el.addEventListener('wheel', handleWheel, { passive: false })
    return () => el.removeEventListener('wheel', handleWheel)
  }, [])

  const decimalToNum = (val) => {
    if (val === null || val === undefined) return 0
    if (typeof val === 'number') return val
    if (typeof val === 'object' && typeof val.toNumber === 'function') return val.toNumber()
    return parseFloat(val) || 0
  }

  const getKeyProp = (key, prop) => {
    const aliases = {
      x: ['x', 'col', 'column', 'cx'],
      y: ['y', 'row', 'ry'],
      w: ['w', 'width', 'w2', 'cw'],
      h: ['h', 'height', 'h2', 'ch'],
      label: ['label', 'text', 'key', 'name', 'cap']
    }
    const candidates = aliases[prop] || []
    for (let alias of candidates) {
      if (key[alias] !== undefined && key[alias] !== null) return key[alias]
    }
    return undefined
  }

  const updateKeyProperty = (index, prop, value) => {
    if (!kleData) return
    let newData = JSON.parse(JSON.stringify(kleData))
    let targetKey = null
    if (Array.isArray(newData[0])) {
      let rowIndex = 0
      let found = false
      for (let r = 0; r < newData.length; r++) {
        for (let c = 0; c < newData[r].length; c++) {
          if (rowIndex === index) {
            targetKey = newData[r][c]
            found = true
            break
          }
          rowIndex++
        }
        if (found) break
      }
    } else {
      targetKey = newData[index]
    }
    if (!targetKey) return

    const propMap = {
      label: 'label',
      w: 'w',
      h: 'h',
      x: 'x',
      y: 'y'
    }
    const actualProp = propMap[prop] || prop
    targetKey[actualProp] = value

    setKleData(newData)
    regeneratePlate(newData)
  }

  const regeneratePlate = (data) => {
    try {
      const plateData = buildPlate(data, {
        switchCutoutType: switchCutoutType,
        stabilizerCutoutType: stabilizerCutoutType,
        acousticCutoutType: acousticCutoutType,
        switchFilletRadius: new Decimal(switchRadius),
        stabilizerFilletRadius: new Decimal(stabilizerRadius),
        acousticFilletRadius: new Decimal(acousticRadius),
        boardRadius: new Decimal(boardRadius),
        unitWidth: new Decimal(unitWidth),
        unitHeight: new Decimal(unitHeight),
        kerf: new Decimal(kerf),
        stabilizerRotation: new Decimal(stabilizerRotation),
        mergeStabilizer: mergeStabilizer,
        plateUseCaseBorders: caseEnabled && plateUseCaseBorders,
        borderTop: new Decimal(caseBorderTop),
        borderBottom: new Decimal(caseBorderBottom),
        borderLeft: new Decimal(caseBorderLeft),
        borderRight: new Decimal(caseBorderRight),
      })

      const previewSvgData = generatePreviewSvg(plateData)
      setPreviewSvg(previewSvgData)

      const svgData = makerjs.exporter.toSVG(plateData, { units: makerjs.unitType.Millimeter, strokeWidth: `${lineWidth}mm` })
      setPlateSvg(svgData)
      const dxfData = makerjs.exporter.toDXF(plateData, { units: makerjs.unitType.Millimeter })
      setPlateDxf(dxfData)
    } catch (error) {
      console.log(error)
    }
  }

  const getSelectedKey = () => {
    if (selectedKeyIndex === null || !kleData) return null
    let flatKeys = kleData
    if (Array.isArray(kleData[0])) {
      flatKeys = kleData.flat()
    }
    return flatKeys[selectedKeyIndex] || null
  }

  const renderKeyLayout = () => {
    if (!kleData || kleData.length === 0) {
      return <div className="text-muted text-center py-4">请在上方粘贴 KLE 数据以预览按键布局</div>
    }

    let flatKeys = kleData
    if (Array.isArray(kleData[0])) {
      flatKeys = kleData.flat()
    }

    const validKeys = flatKeys.filter(k => typeof k === 'object' && k !== null)

    if (validKeys.length === 0) {
      return <div className="text-muted text-center py-4">解析到的数据为空，请检查 KLE 格式</div>
    }

    let maxX = 0, maxY = 0
    validKeys.forEach(key => {
      const x = decimalToNum(getKeyProp(key, 'x'))
      const y = decimalToNum(getKeyProp(key, 'y'))
      const w = decimalToNum(getKeyProp(key, 'w')) || 1
      const h = decimalToNum(getKeyProp(key, 'h')) || 1
      const right = x + w
      const bottom = y + h
      if (right > maxX) maxX = right
      if (bottom > maxY) maxY = bottom
    })

    const unitSize = layoutUnitSize
    const padding = layoutPadding
    const containerWidth = maxX * unitSize + padding * 2
    const containerHeight = maxY * unitSize + padding * 2

    return (
      <div style={{ 
        position: 'relative', 
        width: containerWidth, 
        height: containerHeight, 
        minHeight: '80px',
        margin: '0 auto', 
        padding: `${padding}px`
      }}>
        {validKeys.map((key, index) => {
          const x = decimalToNum(getKeyProp(key, 'x'))
          const y = decimalToNum(getKeyProp(key, 'y'))
          const w = decimalToNum(getKeyProp(key, 'w')) || 1
          const h = decimalToNum(getKeyProp(key, 'h')) || 1
          const angle = decimalToNum(key.angle)
          const rotx = decimalToNum(key.rotx)
          const roty = decimalToNum(key.roty)
          const rawLabel = getKeyProp(key, 'label') || ''
          const label = rawLabel.split('\n')[0] || ''

          const left = x * unitSize + padding
          const top = y * unitSize + padding
          const width = w * unitSize
          const height = h * unitSize

          const originX = (rotx - x) * unitSize
          const originY = (roty - y) * unitSize
          const transform = angle !== 0 ? `rotate(${angle}deg)` : undefined
          const transformOrigin = angle !== 0 ? `${originX}px ${originY}px` : undefined

          let fontSize = 14
          if (width < 30) fontSize = 10
          else if (width < 50) fontSize = 12
          else if (width > 80) fontSize = 16
          else if (height < 25) fontSize = 12
          if (height > 50 && fontSize < 20) fontSize = Math.min(20, fontSize + 2)

          const isSelected = (selectedKeyIndex === index)

          return (
            <div
              key={index}
              className="key"
              style={{
                position: 'absolute',
                left: left,
                top: top,
                width: width,
                height: height,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: fontSize,
                padding: '4px',
                minWidth: 'unset',
                borderRadius: '6px',
                background: isSelected ? '#d4e8ff' : '#f5f5f5',
                color: '#444',
                boxShadow: isSelected 
                  ? '0 0 0 2px #007bff, 0 2px 4px rgba(0,0,0,0.1)' 
                  : '0 2px 4px rgba(0,0,0,0.06), inset 0 -2px 0 #d0d0d0',
                border: isSelected ? '1px solid #007bff' : '1px solid #e8e8e8',
                userSelect: 'none',
                transition: 'all 0.15s',
                cursor: 'pointer',
                lineHeight: 1.3,
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                textAlign: 'center',
                filter: isSelected ? 'drop-shadow(0 0 8px rgba(0, 123, 255, 0.8))' : 'none',
                zIndex: isSelected ? 10 : 'auto',
                transform: transform,
                transformOrigin: transformOrigin,
              }}
              title={rawLabel || label}
              onClick={(e) => { e.stopPropagation(); setSelectedKeyIndex(index) }}
            >
              {label || ''}
            </div>
          )
        })}
      </div>
    )
  }

  const renderEditPanel = () => {
    const selectedKey = getSelectedKey()
    if (!selectedKey) return null

    const x = decimalToNum(getKeyProp(selectedKey, 'x'))
    const y = decimalToNum(getKeyProp(selectedKey, 'y'))
    const w = decimalToNum(getKeyProp(selectedKey, 'w')) || 1
    const h = decimalToNum(getKeyProp(selectedKey, 'h')) || 1
    const label = getKeyProp(selectedKey, 'label') || ''

    const inputStyle = {
      width: '60px',
      height: '28px',
      fontSize: '0.8rem',
      borderRadius: '4px',
      padding: '2px 6px',
      outline: 'none',
    }
    const labelStyle = { margin: 0, fontSize: '0.8rem', fontWeight: 500 }

    return (
      <div 
        className="edit-panel-wrapper" 
        style={{
          borderRadius: '8px',
          padding: '8px 12px',
          background: '#f8faff',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '10px',
          flexWrap: 'wrap',
        }}
      >
        <div className="d-flex align-items-center gap-1">
          <Form.Label className="form-label" style={labelStyle}>标签</Form.Label>
          <Form.Control
            type="text"
            size="sm"
            value={label}
            onChange={e => updateKeyProperty(selectedKeyIndex, 'label', e.target.value)}
            style={{ ...inputStyle, width: '80px' }}
          />
        </div>
        <div className="d-flex align-items-center gap-1">
          <Form.Label className="form-label" style={labelStyle}>宽(U)</Form.Label>
          <NumberInput
            size="sm"
            step={0.25}
            min={0.25}
            value={w}
            onChange={e => updateKeyProperty(selectedKeyIndex, 'w', parseFloat(e.target.value) || 1)}
            style={inputStyle}
          />
        </div>
        <div className="d-flex align-items-center gap-1">
          <Form.Label className="form-label" style={labelStyle}>高(U)</Form.Label>
          <NumberInput
            size="sm"
            step={0.25}
            min={0.25}
            value={h}
            onChange={e => updateKeyProperty(selectedKeyIndex, 'h', parseFloat(e.target.value) || 1)}
            style={inputStyle}
          />
        </div>
        <div className="d-flex align-items-center gap-1">
          <Form.Label className="form-label" style={labelStyle}>X</Form.Label>
          <NumberInput
            size="sm"
            step={0.25}
            value={x}
            onChange={e => updateKeyProperty(selectedKeyIndex, 'x', parseFloat(e.target.value) || 0)}
            style={inputStyle}
          />
        </div>
        <div className="d-flex align-items-center gap-1">
          <Form.Label className="form-label" style={labelStyle}>Y</Form.Label>
          <NumberInput
            size="sm"
            step={0.25}
            value={y}
            onChange={e => updateKeyProperty(selectedKeyIndex, 'y', parseFloat(e.target.value) || 0)}
            style={inputStyle}
          />
        </div>
        <Button variant="outline-secondary" size="sm" onClick={() => setSelectedKeyIndex(null)}>取消选择</Button>
      </div>
    )
  }

  return (
    <div className="app-layout">

      <div className="topbar">
        <div className="topbar-left">
          <a href="https://jlkb.jlkb.top/" target="_blank" rel="noreferrer">
            <Image fluid src={logo} style={{ height: '32px' }} />
          </a>
          <div>
            <h1 className="topbar-title">定位板生成器</h1>
            <p className="topbar-subtitle">支持圆角、切割补偿、精确尺寸及多种开孔类型、轴下垫、底棉、夹心棉等</p>
          </div>
        </div>
      </div>

      <button className="sidebar-toggle" onClick={() => setSidebarOpen(!sidebarOpen)}>
        {sidebarOpen ? '✕' : '☰'}
      </button>
      <div className={`sidebar-overlay ${sidebarOpen ? 'active' : ''}`} onClick={() => setSidebarOpen(false)} />

      <div className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="section-card">
          <div className="section-title">
            <a href="https://www.keyboard-layout-editor.com/" target="_blank" rel="noreferrer" style={{ color: '#6c5ce7', fontWeight: 600, textDecoration: 'none' }}>
              KLE 数据
            </a>
            （前往获取数据）
          </div>
          <Form.Control
            as="textarea"
            style={{ height: '160px' }}
            spellCheck="false"
            placeholder="在此粘贴 KLE 原始数据或 JSON"
            onChange={text => setKleText(text.target.value)}
          />
        </div>

        <div className="section-card">
          <div className="section-title">开孔设置</div>
          <Form>
            <div className="param-row">
              <div className="param-item">
                <Form.Label>轴体类型</Form.Label>
                <Form.Select aria-label="switch-cutout-type" size="sm" onChange={e => setSwitchCutoutType(e.target.value)}>
                  <option value="mx-basic">Cherry MX Basic</option>
                  <option value="alps-skcm">Alps SKCM/L</option>
                  <option value="choc-cpg1350">Kailh Choc CPG1350</option>
                  <option value="choc-cpg1232">Kailh Mini Choc CPG1232</option>
                  <option value="omron-b3g">Omron B3G/B3G-S</option>
                  <option value="alps-skcp">Alps SKCP</option>
                  <option value="hitek-725">Hi-Tek 725</option>
                  <option value="i-rocks">i-Rocks</option>
                  <option value="futaba-ma">Futaba MA</option>
                  <option value="mx-stem">Cherry MX Stem (Cross)</option>
                  <option value="ec-topre-rgb">EC Topre RGB</option>
                  <option value="ec-generic">EC OEM/Generic</option>
                  <option value="ec-dynacap">EC DynaCap</option>
                </Form.Select>
              </div>
              <div className="param-item">
                <Form.Label>卫星轴类型</Form.Label>
                <Form.Select aria-label="stabilizer-cutout-type" size="sm" onChange={e => setStabilizerCutoutType(e.target.value)}>
                  <option value="mx-basic">Cherry MX Basic</option>
                  <option value="mx-small">Cherry MX Tight Fit</option>
                  <option value="mx-spec">Cherry MX Spec</option>
                  <option value="alps-aek">Alps AEK</option>
                  <option value="alps-at101">Alps AT101</option>
                  <option value="ec-topre-rgb">EC Topre RGB</option>
                  <option value="ec-generic">EC OEM/Generic</option>
                  <option value="ec-dynacap">EC DynaCap</option>
                  <option value="none">None</option>
                </Form.Select>
              </div>
            </div>
            <div className="param-row">
              <div className="param-item">
                <Form.Label>卫星轴旋转</Form.Label>
                <NumberInput
                  min={0}
                  max={360}
                  step={1}
                  value={stabilizerRotation}
                  onChange={e => {
                    let val = parseFloat(e.target.value)
                    if (isNaN(val)) val = 0
                    if (val < 0) val = 0
                    if (val > 360) val = 360
                    setStabilizerRotation(val)
                  }}
                  style={{ width: '100%' }}
                />
              </div>
              <div className="param-item">
                <Form.Label>卫星轴合并</Form.Label>
                <SwitchWithStatus 
                  checked={mergeStabilizer} 
                  onChange={setMergeStabilizer} 
                />
              </div>
            </div>
            <div className="param-row">
              <div className="param-item">
                <Form.Label>声学类型</Form.Label>
                <Form.Select aria-label="acoustic-cutout-type" size="sm" onChange={e => setAcousticCutoutType(e.target.value)}>
                  <option value="none">None</option>
                  <option value="mx-basic">Cherry MX Basic</option>
                  <option value="mx-extreme">Cherry MX Extreme</option>
                </Form.Select>
              </div>
              <div className="param-item">
                <Form.Label>轴座类型</Form.Label>
                <Form.Select aria-label="switch-socket-type" size="sm" value={switchSocketType} onChange={e => setSwitchSocketType(e.target.value)}>
                  <option value="kailh">凯华</option>
                  <option value="gateron">高特</option>
                </Form.Select>
              </div>
            </div>
          </Form>
        </div>

        <div className="section-card">
          <div className="section-title">圆角设置</div>
          <Form>
            <div className="param-row">
              <div className="param-item">
                <Form.Label>轴体圆角半径</Form.Label>
                <NumberInput
                  step={0.01}
                  min={0}
                  max={100}
                  precision={3}
                  value={switchRadius}
                  onChange={e => setSwitchRadius(e.target.value)}
                />
              </div>
              <div className="param-item">
                <Form.Label>卫星轴圆角半径</Form.Label>
                <NumberInput
                  step={0.001}
                  min={0}
                  max={100}
                  precision={3}
                  value={stabilizerRadius}
                  onChange={e => setStabilizerRadius(e.target.value)}
                />
              </div>
            </div>
            <div className="param-row">
              <div className="param-item">
                <Form.Label>声学圆角半径</Form.Label>
                <NumberInput
                  step={0.001}
                  min={0}
                  max={100}
                  precision={3}
                  value={acousticRadius}
                  onChange={e => setAcousticRadius(e.target.value)}
                />
              </div>
              <div className="param-item">
                <Form.Label>板框圆角半径</Form.Label>
                <NumberInput
                  step={0.001}
                  min={0}
                  max={100}
                  precision={3}
                  value={boardRadius}
                  onChange={e => setBoardRadius(e.target.value)}
                />
              </div>
            </div>
          </Form>
        </div>

        <div className="section-card">
          <div className="section-title">高级设置</div>
          <Form>
            <div className="param-row">
              <div className="param-item">
                <Form.Label>单位宽度 (mm)</Form.Label>
                <NumberInput
                  step={0.001}
                  min={0}
                  max={100}
                  precision={3}
                  value={unitWidth}
                  onChange={e => setUnitWidth(e.target.value)}
                />
              </div>
              <div className="param-item">
                <Form.Label>单位高度 (mm)</Form.Label>
                <NumberInput
                  step={0.001}
                  min={0}
                  max={100}
                  precision={3}
                  value={unitHeight}
                  onChange={e => setUnitHeight(e.target.value)}
                />
              </div>
            </div>
            <div className="param-row">
              <div className="param-item">
                <Form.Label>切割补偿 (mm)</Form.Label>
                <NumberInput
                  step={0.001}
                  min={0}
                  max={100}
                  precision={3}
                  value={kerf}
                  onChange={e => setKerf(e.target.value)}
                />
              </div>
              <div className="param-item">
                <Form.Label>预览线宽 (mm)</Form.Label>
                <NumberInput
                  step={0.001}
                  min={0.1}
                  max={10}
                  precision={3}
                  value={lineWidth}
                  onChange={e => setLineWidth(e.target.value)}
                />
              </div>
            </div>
            <div className="param-row">
              <div className="param-item">
                <Form.Label>灯位切换</Form.Label>
                <SwitchWithStatus
                  checked={ledPosition === 'bottom'}
                  onChange={val => setLedPosition(val ? 'bottom' : 'top')}
                  leftLabel="下"
                  rightLabel="上"
                />
              </div>
              <div className="param-item">
                <Form.Label>3D 预览</Form.Label>
                <SwitchWithStatus
                  checked={preview3dEnabled}
                  onChange={setPreview3dEnabled}
                  leftLabel="关"
                  rightLabel="开"
                />
              </div>
            </div>
            <div className="param-row">
              <div className="param-item">
                <Form.Label>预览背景</Form.Label>
                <SwitchWithStatus
                  checked={previewLightBg}
                  onChange={setPreviewLightBg}
                  leftLabel="浅"
                  rightLabel="深"
                />
              </div>
            </div>
            {preview3dEnabled && (
              <div className="param-row">
                <div className="param-item">
                  <Form.Label>厚度 (mm)</Form.Label>
                  <NumberInput
                    min={0.1}
                    max={100}
                    step={0.1}
                    precision={4}
                    value={preview3dThickness}
                    onChange={e => {
                      let val = parseFloat(e.target.value)
                      if (isNaN(val)) val = 1.5
                      if (val < 0.1) val = 0.1
                      if (val > 100) val = 100
                      setPreview3dThickness(val)
                    }}
                    style={{ width: '100%' }}
                  />
                </div>
                <div className="param-item">
                  <Form.Label>颜色</Form.Label>
                  <Form.Control
                    type="color"
                    value={preview3dColor}
                    onChange={e => setPreview3dColor(e.target.value)}
                    size="sm"
                    style={{ width: '100%', height: '32px', padding: '2px' }}
                  />
                </div>
              </div>
            )}
          </Form>
        </div>

        <div className="section-card">
          <div className="section-title">外壳设置（堆叠）</div>
          <Form>
            <div className="param-row">
              <div className="param-item">
                <SwitchWithStatus
                  checked={caseEnabled}
                  onChange={setCaseEnabled}
                  leftLabel="关"
                  rightLabel="开"
                />
              </div>
            </div>
            {caseEnabled && (
              <>
                <div className="param-row">
                  <div className="param-item">
                    <Form.Label>定位板偏移</Form.Label>
                    <SwitchWithStatus
                      checked={plateUseCaseBorders}
                      onChange={setPlateUseCaseBorders}
                      leftLabel="开"
                      rightLabel="关"
                    />
                  </div>
                </div>
                <div className="param-row">
                  <div className="param-item">
                    <Form.Label>上边框宽度 (mm)</Form.Label>
                    <NumberInput
                      step={0.1}
                      min={1}
                      max={10}
                      precision={4}
                      value={caseBorderTop}
                      onChange={e => setCaseBorderTop(e.target.value)}
                    />
                  </div>
                  <div className="param-item">
                    <Form.Label>下边框宽度 (mm)</Form.Label>
                    <NumberInput
                      step={0.1}
                      min={1}
                      max={10}
                      precision={4}
                      value={caseBorderBottom}
                      onChange={e => setCaseBorderBottom(e.target.value)}
                    />
                  </div>
                </div>
                <div className="param-row">
                  <div className="param-item">
                    <Form.Label>左边框宽度 (mm)</Form.Label>
                    <NumberInput
                      step={0.1}
                      min={1}
                      max={10}
                      precision={4}
                      value={caseBorderLeft}
                      onChange={e => setCaseBorderLeft(e.target.value)}
                    />
                  </div>
                  <div className="param-item">
                    <Form.Label>右边框宽度 (mm)</Form.Label>
                    <NumberInput
                      step={0.1}
                      min={1}
                      max={10}
                      precision={4}
                      value={caseBorderRight}
                      onChange={e => setCaseBorderRight(e.target.value)}
                    />
                  </div>
                </div>
                <div className="param-row">
                  <div className="param-item">
                    <Form.Label>内框圆角半径 (mm)</Form.Label>
                    <NumberInput
                      step={0.001}
                      min={0}
                      max={100}
                      precision={3}
                      value={caseInnerRadius}
                      onChange={e => setCaseInnerRadius(e.target.value)}
                    />
                  </div>
                </div>
              </>
            )}
          </Form>
        </div>

        <div className="section-card">
          <button className="help-toggle-btn" onClick={() => setHelpOpen(!helpOpen)}>
            {helpOpen ? '▼ 收起帮助信息' : '▶ 展开帮助信息'}
          </button>
          <Collapse in={helpOpen}>
            <div className="help-collapse">
              <DataHelpPane />
              <SwitchCutoutPane />
              <OtherCutoutPane />
              <AdvancedPane />
              <AboutPane />
            </div>
          </Collapse>
        </div>
      </div>

      <div className="main-area">
        <div className="section-card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', borderBottom: '1px solid #e8e8e8', background: '#fafafa' }}>
            <div className="d-flex gap-2">
              <Button variant={previewMode === 'plate' ? 'primary' : 'outline-secondary'} size="sm" onClick={() => setPreviewMode('plate')}>定位板</Button>
              <Button variant={previewMode === 'sandwich' ? 'primary' : 'outline-secondary'} size="sm" onClick={() => setPreviewMode('sandwich')}>夹心棉</Button>
              <Button variant={previewMode === 'pad' ? 'primary' : 'outline-secondary'} size="sm" onClick={() => setPreviewMode('pad')}>轴下垫</Button>
              <Button variant={previewMode === 'foam' ? 'primary' : 'outline-secondary'} size="sm" onClick={() => setPreviewMode('foam')}>底棉</Button>
              {caseEnabled && (
                <>
                  <Button variant={previewMode === 'case-layer1' ? 'primary' : 'outline-secondary'} size="sm" onClick={() => setPreviewMode('case-layer1')}>层1</Button>
                  <Button variant={previewMode === 'case-layer2' ? 'primary' : 'outline-secondary'} size="sm" onClick={() => setPreviewMode('case-layer2')}>层2</Button>
                  <Button variant={previewMode === 'case-layer3' ? 'primary' : 'outline-secondary'} size="sm" onClick={() => setPreviewMode('case-layer3')}>层3</Button>
                </>
              )}
            </div>
            <div className="d-flex gap-2">
              {previewMode === 'plate' && (
                <>
                  <Button variant="outline-secondary" size="sm" disabled={!plateDxf} onClick={() => downloadData(plateDxf, '.dxf')}>下载 DXF</Button>
                  <Button variant="outline-secondary" size="sm" disabled={!plateSvg} onClick={() => downloadData(plateSvg, '.svg')}>下载 SVG</Button>
                  <Button variant="outline-primary" size="sm" disabled={!plateDxf} onClick={() => openCadEditor(plateDxf)}>编辑 DXF</Button>
                </>
              )}
              {previewMode === 'sandwich' && (
                <>
                  <Button variant="outline-secondary" size="sm" disabled={!sandwichDxf} onClick={() => downloadSandwichData(sandwichDxf, '.dxf')}>下载 DXF</Button>
                  <Button variant="outline-secondary" size="sm" disabled={!sandwichSvg} onClick={() => downloadSandwichData(sandwichSvg, '.svg')}>下载 SVG</Button>
                  <Button variant="outline-primary" size="sm" disabled={!sandwichDxf} onClick={() => openCadEditor(sandwichDxf)}>编辑 DXF</Button>
                </>
              )}
              {previewMode === 'pad' && (
                <>
                  <Button variant="outline-secondary" size="sm" disabled={!padDxf} onClick={() => downloadPadData(padDxf, '.dxf')}>下载 DXF</Button>
                  <Button variant="outline-secondary" size="sm" disabled={!padSvg} onClick={() => downloadPadData(padSvg, '.svg')}>下载 SVG</Button>
                  <Button variant="outline-primary" size="sm" disabled={!padDxf} onClick={() => openCadEditor(padDxf)}>编辑 DXF</Button>
                </>
              )}
              {previewMode === 'foam' && (
                <>
                  <Button variant="outline-secondary" size="sm" disabled={!foamDxf} onClick={() => downloadFoamData(foamDxf, '.dxf')}>下载 DXF</Button>
                  <Button variant="outline-secondary" size="sm" disabled={!foamSvg} onClick={() => downloadFoamData(foamSvg, '.svg')}>下载 SVG</Button>
                  <Button variant="outline-primary" size="sm" disabled={!foamDxf} onClick={() => openCadEditor(foamDxf)}>编辑 DXF</Button>
                </>
              )}
              {previewMode === 'case-layer1' && (
                <>
                  <Button variant="outline-secondary" size="sm" disabled={!caseLayer1Dxf} onClick={() => downloadCaseData(caseLayer1Dxf, '.dxf', '层1')}>下载 DXF</Button>
                  <Button variant="outline-secondary" size="sm" disabled={!caseLayer1Svg} onClick={() => downloadCaseData(caseLayer1Svg, '.svg', '层1')}>下载 SVG</Button>
                  <Button variant="outline-primary" size="sm" disabled={!caseLayer1Dxf} onClick={() => openCadEditor(caseLayer1Dxf)}>编辑 DXF</Button>
                </>
              )}
              {previewMode === 'case-layer2' && (
                <>
                  <Button variant="outline-secondary" size="sm" disabled={!caseLayer2Dxf} onClick={() => downloadCaseData(caseLayer2Dxf, '.dxf', '层2')}>下载 DXF</Button>
                  <Button variant="outline-secondary" size="sm" disabled={!caseLayer2Svg} onClick={() => downloadCaseData(caseLayer2Svg, '.svg', '层2')}>下载 SVG</Button>
                  <Button variant="outline-primary" size="sm" disabled={!caseLayer2Dxf} onClick={() => openCadEditor(caseLayer2Dxf)}>编辑 DXF</Button>
                </>
              )}
              {previewMode === 'case-layer3' && (
                <>
                  <Button variant="outline-secondary" size="sm" disabled={!caseLayer3Dxf} onClick={() => downloadCaseData(caseLayer3Dxf, '.dxf', '层3')}>下载 DXF</Button>
                  <Button variant="outline-secondary" size="sm" disabled={!caseLayer3Svg} onClick={() => downloadCaseData(caseLayer3Svg, '.svg', '层3')}>下载 SVG</Button>
                  <Button variant="outline-primary" size="sm" disabled={!caseLayer3Dxf} onClick={() => openCadEditor(caseLayer3Dxf)}>编辑 DXF</Button>
                </>
              )}
            </div>
          </div>
          <div
            ref={previewContainerRef}
            className="preview-container"
            style={{
              background: previewLightBg ? '#f0f2f5' : undefined,
            }}
            onMouseDown={preview3dEnabled ? undefined : handleMouseDown}
            onMouseMove={preview3dEnabled ? undefined : handleMouseMove}
            onMouseUp={preview3dEnabled ? undefined : handleMouseUp}
            onMouseLeave={preview3dEnabled ? undefined : handleMouseUp}
          >
            {preview3dEnabled ? (
              <ThreePreview
                svgContent={
                  previewMode === 'plate' ? previewSvg :
                  previewMode === 'sandwich' ? sandwichPreviewSvg :
                  previewMode === 'pad' ? padPreviewSvg :
                  previewMode === 'foam' ? foamPreviewSvg :
                  previewMode === 'case-layer1' ? caseLayer1Svg :
                  previewMode === 'case-layer2' ? caseLayer2Svg :
                  previewMode === 'case-layer3' ? caseLayer3Svg :
                  null
                }
                thickness={preview3dThickness}
                color={preview3dColor}
                resetKey={preview3dResetKey}
                lightBg={previewLightBg}
              />
            ) : (
              <div style={{
                transform: `translate(${previewPosition.x}px, ${previewPosition.y}px) scale(${previewScale})`,
                transformOrigin: 'center center',
                width: '100%',
                height: '100%',
                transition: isDragging ? 'none' : 'transform 0.1s ease-out'
              }} dangerouslySetInnerHTML={{ __html:
                previewMode === 'plate' ? previewSvg :
                previewMode === 'sandwich' ? sandwichPreviewSvg :
                previewMode === 'pad' ? padPreviewSvg :
                previewMode === 'foam' ? foamPreviewSvg :
                previewMode === 'case-layer1' ? caseLayer1Svg :
                previewMode === 'case-layer2' ? caseLayer2Svg :
                previewMode === 'case-layer3' ? caseLayer3Svg :
                null
              }} />
            )}
            {/* 浅色背景下尺寸信息：黑字 + 浅灰底 */}
            {(() => {
              const lightBadgeStyle = previewLightBg
                ? { color: '#111', background: '#e5e7eb', borderColor: '#d1d5db' }
                : undefined
              return (
                <>
                  <div className="preview-scale-badge" style={lightBadgeStyle}>
                    {preview3dEnabled ? '3D' : `${Math.round(previewScale * 100)}%`}
                  </div>
                  {previewMode === 'plate' && plateDimensions && (
                    <div className="preview-dim-badge" style={lightBadgeStyle}>
                      长: {plateDimensions.width.toFixed(4)} mm<br />
                      宽: {plateDimensions.height.toFixed(4)} mm<br />
                      面积: {plateDimensions.area.toFixed(4)} mm²<br />
                      总线长: {plateDimensions.totalLength.toFixed(4)} mm
                    </div>
                  )}
                  {previewMode === 'sandwich' && sandwichDimensions && (
                    <div className="preview-dim-badge" style={lightBadgeStyle}>
                      长: {sandwichDimensions.width.toFixed(4)} mm<br />
                      宽: {sandwichDimensions.height.toFixed(4)} mm<br />
                      面积: {sandwichDimensions.area.toFixed(4)} mm²<br />
                      总线长: {sandwichDimensions.totalLength.toFixed(4)} mm
                    </div>
                  )}
                  {previewMode === 'pad' && padDimensions && (
                    <div className="preview-dim-badge" style={lightBadgeStyle}>
                      长: {padDimensions.width.toFixed(4)} mm<br />
                      宽: {padDimensions.height.toFixed(4)} mm<br />
                      面积: {padDimensions.area.toFixed(4)} mm²<br />
                      总线长: {padDimensions.totalLength.toFixed(4)} mm<br />
                      {ledPosition === 'top' ? '上灯位' : '下灯位 (180°)'}
                    </div>
                  )}
                  {previewMode === 'foam' && foamDimensions && (
                    <div className="preview-dim-badge" style={lightBadgeStyle}>
                      长: {foamDimensions.width.toFixed(4)} mm<br />
                      宽: {foamDimensions.height.toFixed(4)} mm<br />
                      面积: {foamDimensions.area.toFixed(4)} mm²<br />
                      总线长: {foamDimensions.totalLength.toFixed(4)} mm<br />
                      {ledPosition === 'top' ? '上灯位' : '下灯位 (180°)'}
                    </div>
                  )}
                  {previewMode === 'case-layer1' && layer1Dimensions && (
                    <div className="preview-dim-badge" style={lightBadgeStyle}>
                      长: {layer1Dimensions.width.toFixed(4)} mm<br />
                      宽: {layer1Dimensions.height.toFixed(4)} mm<br />
                      面积: {layer1Dimensions.area.toFixed(4)} mm²<br />
                      总线长: {layer1Dimensions.totalLength.toFixed(4)} mm
                    </div>
                  )}
                  {previewMode === 'case-layer2' && layer2Dimensions && (
                    <div className="preview-dim-badge" style={lightBadgeStyle}>
                      长: {layer2Dimensions.width.toFixed(4)} mm<br />
                      宽: {layer2Dimensions.height.toFixed(4)} mm<br />
                      面积: {layer2Dimensions.area.toFixed(4)} mm²<br />
                      总线长: {layer2Dimensions.totalLength.toFixed(4)} mm
                    </div>
                  )}
                  {previewMode === 'case-layer3' && layer3Dimensions && (
                    <div className="preview-dim-badge" style={lightBadgeStyle}>
                      长: {layer3Dimensions.width.toFixed(4)} mm<br />
                      宽: {layer3Dimensions.height.toFixed(4)} mm<br />
                      面积: {layer3Dimensions.area.toFixed(4)} mm²<br />
                      总线长: {layer3Dimensions.totalLength.toFixed(4)} mm
                    </div>
                  )}
                  <button
                    className="preview-reset-btn"
                    style={lightBadgeStyle}
                    onClick={() => {
                      setPreviewScale(1)
                      setPreviewPosition({ x: 0, y: 0 })
                      if (preview3dEnabled) {
                        setPreview3dResetKey(k => k + 1)
                      }
                    }}
                    title="重置视图"
                  >
                    ⟲ 重置
                  </button>
                  {!previewSvg && !sandwichPreviewSvg && !padPreviewSvg && !foamPreviewSvg && (
                    <div className="preview-hint" style={previewLightBg ? { color: '#333' } : undefined}>
                      粘贴 KLE 数据后自动生成预览 · 滚轮缩放 · 拖拽平移
                    </div>
                  )}
                </>
              )
            })()}
          </div>
        </div>

        <div className="section-card">
          <div className="layout-header">
            <div className="layout-header-left">
              <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>按键布局预览</span>
              {selectedKeyIndex !== null && (
                <span className="badge" style={{ background: '#4f46e5', fontSize: '0.72rem' }}>已选中 #{selectedKeyIndex + 1}</span>
              )}
            </div>
            <div className="layout-controls">
              {selectedKeyIndex !== null && renderEditPanel()}
              {selectedKeyIndex === null && (
                <>
                  <div className="d-flex align-items-center gap-1">
                    <Form.Label>单位</Form.Label>
                    <NumberInput
                      min={10}
                      max={100}
                      step={1}
                      value={layoutUnitSize}
                      onChange={e => setLayoutUnitSize(Number(e.target.value) || 40)}
                    />
                  </div>
                  <div className="d-flex align-items-center gap-1">
                    <Form.Label>内边距</Form.Label>
                    <NumberInput
                      min={0}
                      max={50}
                      step={1}
                      value={layoutPadding}
                      onChange={e => setLayoutPadding(Number(e.target.value) || 10)}
                    />
                  </div>
                </>
              )}
            </div>
          </div>
          <div style={{ overflow: 'auto', minHeight: '80px', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setSelectedKeyIndex(null)}>
            <div style={{ width: 'fit-content', background: '#f8f9fa', borderRadius: '8px', padding: '10px' }}>
              {renderKeyLayout()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;